import React, { useState } from 'react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Plus, X, Save } from 'lucide-react';
interface ApprovalCondition {
  id: string;
  fieldName: string;
  dataType: 'numeric' | 'text' | 'dropdown';
  condition: string;
  fieldValue: string;
  logicalOperator: 'AND' | 'OR' | '';
}
interface RejectionCondition {
  id: string;
  fieldName: string;
  dataType: 'numeric' | 'text' | 'dropdown';
  condition: string;
  fieldValue: string;
  rejectionAction: string;
  logicalOperator: 'AND' | 'OR' | '';
}
export function RuleBuilderTab() {
  const [approvalConditions, setApprovalConditions] = useState<ApprovalCondition[]>([{
    id: '1',
    fieldName: '',
    dataType: 'numeric',
    condition: '',
    fieldValue: '',
    logicalOperator: ''
  }]);
  const [rejectionConditions, setRejectionConditions] = useState<RejectionCondition[]>([{
    id: '1',
    fieldName: '',
    dataType: 'numeric',
    condition: '',
    fieldValue: '',
    rejectionAction: '',
    logicalOperator: ''
  }]);
  const fieldOptions = [{
    value: '',
    label: 'Select field...'
  }, {
    value: 'credit_score',
    label: 'Credit Score',
    dataType: 'numeric'
  }, {
    value: 'income',
    label: 'Monthly Income',
    dataType: 'numeric'
  }, {
    value: 'age',
    label: 'Age',
    dataType: 'numeric'
  }, {
    value: 'loan_amount',
    label: 'Loan Amount',
    dataType: 'numeric'
  }, {
    value: 'region',
    label: 'Region',
    dataType: 'dropdown'
  }, {
    value: 'employment_type',
    label: 'Employment Type',
    dataType: 'dropdown'
  }, {
    value: 'customer_name',
    label: 'Customer Name',
    dataType: 'text'
  }, {
    value: 'cnic',
    label: 'CNIC',
    dataType: 'text'
  }];
  const numericConditions = [{
    value: '',
    label: 'Select condition...'
  }, {
    value: 'equals',
    label: 'Equals to'
  }, {
    value: 'not_equals',
    label: 'NOT Equals to'
  }, {
    value: 'greater_than',
    label: 'Greater than'
  }, {
    value: 'less_than',
    label: 'Less than'
  }, {
    value: 'greater_equal',
    label: 'Greater than or Equal to'
  }, {
    value: 'less_equal',
    label: 'Less than or Equal to'
  }];
  const textConditions = [{
    value: '',
    label: 'Select condition...'
  }, {
    value: 'equals',
    label: 'Equals to'
  }, {
    value: 'not_equals',
    label: 'NOT Equals to'
  }, {
    value: 'contains',
    label: 'Contains'
  }, {
    value: 'not_contains',
    label: 'NOT Contains'
  }, {
    value: 'starts_with',
    label: 'Starts with'
  }, {
    value: 'ends_with',
    label: 'Ends with'
  }];
  const dropdownConditions = [{
    value: '',
    label: 'Select condition...'
  }, {
    value: 'equals',
    label: 'Equals to'
  }, {
    value: 'not_equals',
    label: 'NOT Equals to'
  }];
  const regionOptions = [{
    value: '',
    label: 'Select region...'
  }, {
    value: 'punjab',
    label: 'Punjab'
  }, {
    value: 'sindh',
    label: 'Sindh'
  }, {
    value: 'kpk',
    label: 'KPK'
  }, {
    value: 'balochistan',
    label: 'Balochistan'
  }];
  const employmentOptions = [{
    value: '',
    label: 'Select employment type...'
  }, {
    value: 'salaried',
    label: 'Salaried'
  }, {
    value: 'self_employed',
    label: 'Self Employed'
  }, {
    value: 'business',
    label: 'Business Owner'
  }];
  const rejectionActionOptions = [{
    value: '',
    label: 'Select action...'
  }, {
    value: 'reject',
    label: 'Reject Application'
  }, {
    value: 'send_notification',
    label: 'Send Notification'
  }, {
    value: 'manual_review',
    label: 'Send for Manual Review'
  }];
  const logicalOperatorOptions = [{
    value: 'AND',
    label: 'AND'
  }, {
    value: 'OR',
    label: 'OR'
  }];
  // Approval Condition Handlers
  const addApprovalCondition = () => {
    setApprovalConditions([...approvalConditions, {
      id: Date.now().toString(),
      fieldName: '',
      dataType: 'numeric',
      condition: '',
      fieldValue: '',
      logicalOperator: 'AND'
    }]);
  };
  const removeApprovalCondition = (id: string) => {
    if (approvalConditions.length > 1) {
      setApprovalConditions(approvalConditions.filter(c => c.id !== id));
    }
  };
  const updateApprovalCondition = (id: string, field: keyof ApprovalCondition, value: any) => {
    setApprovalConditions(approvalConditions.map(c => {
      if (c.id === id) {
        const updated = {
          ...c,
          [field]: value
        };
        // Update dataType when fieldName changes
        if (field === 'fieldName') {
          const selectedField = fieldOptions.find(f => f.value === value);
          if (selectedField && 'dataType' in selectedField) {
            updated.dataType = selectedField.dataType as any;
            updated.condition = '';
            updated.fieldValue = '';
          }
        }
        return updated;
      }
      return c;
    }));
  };
  // Rejection Condition Handlers
  const addRejectionCondition = () => {
    setRejectionConditions([...rejectionConditions, {
      id: Date.now().toString(),
      fieldName: '',
      dataType: 'numeric',
      condition: '',
      fieldValue: '',
      rejectionAction: '',
      logicalOperator: 'AND'
    }]);
  };
  const removeRejectionCondition = (id: string) => {
    if (rejectionConditions.length > 1) {
      setRejectionConditions(rejectionConditions.filter(c => c.id !== id));
    }
  };
  const updateRejectionCondition = (id: string, field: keyof RejectionCondition, value: any) => {
    setRejectionConditions(rejectionConditions.map(c => {
      if (c.id === id) {
        const updated = {
          ...c,
          [field]: value
        };
        // Update dataType when fieldName changes
        if (field === 'fieldName') {
          const selectedField = fieldOptions.find(f => f.value === value);
          if (selectedField && 'dataType' in selectedField) {
            updated.dataType = selectedField.dataType as any;
            updated.condition = '';
            updated.fieldValue = '';
          }
        }
        return updated;
      }
      return c;
    }));
  };
  const getConditionOptions = (dataType: string) => {
    switch (dataType) {
      case 'numeric':
        return numericConditions;
      case 'text':
        return textConditions;
      case 'dropdown':
        return dropdownConditions;
      default:
        return numericConditions;
    }
  };
  const renderFieldValueInput = (condition: ApprovalCondition | RejectionCondition, isRejection: boolean) => {
    const updateFn = isRejection ? updateRejectionCondition : updateApprovalCondition;
    if (condition.dataType === 'dropdown') {
      let options = [];
      if (condition.fieldName === 'region') {
        options = regionOptions;
      } else if (condition.fieldName === 'employment_type') {
        options = employmentOptions;
      }
      return <Select value={condition.fieldValue} onChange={e => updateFn(condition.id, 'fieldValue', e.target.value)} options={options} data-id="element-883" />;
    } else if (condition.dataType === 'numeric') {
      return <Input type="number" value={condition.fieldValue} onChange={e => updateFn(condition.id, 'fieldValue', e.target.value)} placeholder="Enter value" data-id="element-884" />;
    } else {
      return <Input type="text" value={condition.fieldValue} onChange={e => updateFn(condition.id, 'fieldValue', e.target.value)} placeholder="Enter value or regex pattern" data-id="element-885" />;
    }
  };
  const handleSaveRule = () => {
    console.log('Rejection Conditions:', rejectionConditions);
    // In real app, this would save to backend
  };
  return <div className="space-y-8 max-w-6xl animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-886">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-887">
        <div data-id="element-888">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-889">Rule Builder</h2>
          <p className="text-sm text-gray-500 mt-1" data-id="element-890">
            Define rejection conditions for automated decision making
          </p>
        </div>
        <Button onClick={handleSaveRule} data-id="element-891">
          <Save className="h-4 w-4 mr-2" data-id="element-892" />
          Save Rule
        </Button>
      </div>

      {/* Rejection Conditions Section */}
      <section className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-893">
        <div className="flex items-center justify-between mb-6" data-id="element-894">
          <div data-id="element-895">
            <h3 className="text-lg font-semibold text-gray-900" data-id="element-896">
              Rejection Conditions
            </h3>
            <p className="text-sm text-gray-500 mt-1" data-id="element-897">
              Define conditions that will trigger automatic rejection
            </p>
          </div>
          <Button variant="secondary" size="sm" onClick={addRejectionCondition} data-id="element-898">
            <Plus className="h-4 w-4 mr-2" data-id="element-899" />
            Add Condition
          </Button>
        </div>

        <div className="space-y-4" data-id="element-900">
          {rejectionConditions.map((condition, index) => <div key={condition.id} className="bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-901">
              <div className="grid grid-cols-12 gap-4" data-id="element-902">
                {/* Logical Operator */}
                {index > 0 && <div className="col-span-12 md:col-span-2" data-id="element-903">
                    <Select label="Operator" value={condition.logicalOperator} onChange={e => updateRejectionCondition(condition.id, 'logicalOperator', e.target.value)} options={logicalOperatorOptions} data-id="element-904" />
                  </div>}

                {/* Field Name */}
                <div className={`col-span-12 ${index > 0 ? 'md:col-span-2' : 'md:col-span-3'}`} data-id="element-905">
                  <Select label="Field Name" value={condition.fieldName} onChange={e => updateRejectionCondition(condition.id, 'fieldName', e.target.value)} options={fieldOptions} data-id="element-906" />
                </div>

                {/* Condition */}
                <div className="col-span-12 md:col-span-2" data-id="element-907">
                  <Select label="Condition" value={condition.condition} onChange={e => updateRejectionCondition(condition.id, 'condition', e.target.value)} options={getConditionOptions(condition.dataType)} data-id="element-908" />
                </div>

                {/* Field Value */}
                <div className="col-span-12 md:col-span-2" data-id="element-909">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-910">
                    Field Value
                  </label>
                  {renderFieldValueInput(condition, true)}
                </div>

                {/* Rejection Action */}
                <div className="col-span-12 md:col-span-3" data-id="element-911">
                  <Select label="Rejection Action" value={condition.rejectionAction} onChange={e => updateRejectionCondition(condition.id, 'rejectionAction', e.target.value)} options={rejectionActionOptions} data-id="element-912" />
                </div>

                {/* Remove Button */}
                <div className="col-span-12 md:col-span-1 flex items-end justify-end" data-id="element-913">
                  <Button variant="ghost" size="sm" onClick={() => removeRejectionCondition(condition.id)} disabled={rejectionConditions.length === 1} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-id="element-914">
                    <X className="h-4 w-4" data-id="element-915" />
                  </Button>
                </div>
              </div>
            </div>)}
        </div>
      </section>
    </div>;
}